import {Inject, Injectable, Optional, SkipSelf} from '@angular/core';
import {from, of, concat, Observable, throwError, EMPTY} from 'rxjs';
import {filter, first, map, mergeMap, switchMap, throwIfEmpty, toArray} from 'rxjs/operators';

import {
  DynamicResource,
  DynamicResourceUrlResolver,
  DYNAMIC_RESOURCE_URL_RESOLVERS,
} from '../interfaces/resource-resolver';

@Injectable()
export class CombinedDynamicResourceUrlResolver implements DynamicResourceUrlResolver {
  private readonly _resolvers: DynamicResourceUrlResolver[];

  public constructor(
    @Inject(DYNAMIC_RESOURCE_URL_RESOLVERS) @Optional() resolvers?: DynamicResourceUrlResolver[],
    @Optional() @SkipSelf() private readonly parent?: CombinedDynamicResourceUrlResolver,
  ) {
    this._resolvers = resolvers != null ? resolvers.slice() : [];
  }

  public resolve(resource: DynamicResource): Observable<URL> {
    let resolution = from(this._resolvers).pipe(
      mergeMap(resolver => {
        const request = resolver.resolve(resource);

        if (request == null || request instanceof URL) {
          return of([request, resolver] as const);
        }

        return from(request).pipe(map(request => [request, resolver] as const));
      }),
      filter((request): request is [URL, DynamicResourceUrlResolver] => request[0] != null),
      toArray(),
      switchMap(requests => {
        switch (requests.length) {
          case 0:
            return EMPTY;
          case 1:
            return of(requests[0][0]);
          default:
            return throwError(
              new Error(
                `More than one registered DynamicResourceUrlResolver provided a value for resource with identifier ${JSON.stringify(
                  resource.identifier,
                )}: ${requests.map(([, resolver]) => resolver.constructor.name).join(', ')}`,
              ),
            );
        }
      }),
    );

    if (this.parent != null) {
      resolution = concat(resolution, this.parent.resolve(resource)).pipe(first());
    }

    return resolution.pipe(
      throwIfEmpty(
        () =>
          new Error(
            `No registered DynamicResourceUrlResolver provided a value for resource with identifier ${JSON.stringify(
              resource.identifier,
            )}`,
          ),
      ),
    );
  }
}
